import React, { useEffect, useRef, useState } from "react";

import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import {
  toFileKey,
  uploadToS3 as uploadFileWithPresigned,
} from "shared/api/awsS3";
import { useDirtyCheck } from "shared/lib/form/useDirtyCheck";
import CircularProgressIndicator from "shared/ui/CircularProgressIndicator";
import { alertDialog, confirmDialog, confirmLeaveIfDirty } from "shared/ui/feedback";
import { ChipGroup } from "shared/ui/filters";
import { FormPageShell } from "shared/ui/layout";
import { ImageUploadBox } from "shared/ui/upload";

const SHOP_CONTENT_MAX_LENGTH = 501;
const SHOP_ENG_CONTENT_MAX_LENGTH = 701;
const CATEGORIES = ["식사", "카페", "피트니스", "미용", "기타"];

const COPY = {
  create: {
    header: "HGU샵 등록하기",
    submit: "등록하기",
    confirm: "HGU샵을 등록하시겠습니까?\n해당 내용으로 등록됩니다.",
    leave: "HGU샵 등록을 취소하시겠습니까?\n취소시 작성 중인 내용이 사라집니다.",
    successAlert: "HGU샵이 등록되었습니다.",
  },
  edit: {
    header: "HGU샵 수정",
    submit: "수정하기",
    confirm: "해당 HGU샵을 수정하시겠습니까?",
    leave: "HGU 등록 설정을 취소하시겠습니까?\n취소시 수정 중인 내용이 취소됩니다.",
    successAlert: null,
  },
};

const buildEditorConfig = (placeholder) => ({
  placeholder,
  toolbar: {
    items: [
      "heading",
      "|",
      "bold",
      "italic",
      "link",
      "bulletedList",
      "numberedList",
      "blockQuote",
    ],
  },
});

/**
 * HGU샵 등록·수정 공용 폼.
 *
 * 생성/수정의 차이는 (1)초기값 (2)이미지 정책(기존 URL 유지 vs 신규 업로드)
 * (3)제출 API (4)문구뿐이라 props로 주입받아 한 컴포넌트로 처리한다.
 *
 * @param {"create"|"edit"} mode
 * @param {object|null} initialValues - {name, title, category, content, engTitle, engContent, pin, inProgress, image}
 * @param {boolean} isLoading
 * @param {string}  loadError
 * @param {(data:object)=>Promise<void>} onSubmit - 생성/수정 API 호출
 */
export function ShopForm({
  mode,
  initialValues = null,
  isLoading = false,
  loadError = "",
  onSubmit,
}) {
  const copy = COPY[mode];
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [content, setContent] = useState("");
  const [engTitle, setEngTitle] = useState("");
  const [engContent, setEngContent] = useState("");
  const [pin, setPin] = useState(false);
  const [inProgress, setInProgress] = useState(true); // true=제휴중, false=종료
  const [imageView, setImageView] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const { isDirty, captureBaseline } = useDirtyCheck({
    title,
    category: selectedCategory,
    content,
    image: imageView || "",
    pin,
    inProgress,
    engTitle,
    engContent,
    name,
  });

  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current || !initialValues) return;
    initializedRef.current = true;
    setName(initialValues.name || "");
    setTitle(initialValues.title || "");
    setSelectedCategory(initialValues.category || "");
    setContent(initialValues.content || "");
    setEngTitle(initialValues.engTitle || "");
    setEngContent(initialValues.engContent || "");
    setPin(Boolean(initialValues.pin));
    // 레거시 데이터의 inProgress=null은 제휴중(true)으로 간주(저장 시 확정 boolean으로 정리).
    setInProgress(initialValues.inProgress ?? true);
    setImageView(initialValues.image || "");
    setFile(null);
    captureBaseline({
      title: initialValues.title || "",
      category: initialValues.category || "",
      content: initialValues.content || "",
      image: initialValues.image || "",
      pin: Boolean(initialValues.pin),
      inProgress: initialValues.inProgress ?? true,
      engTitle: initialValues.engTitle || "",
      engContent: initialValues.engContent || "",
      name: initialValues.name || "",
    });
  }, [initialValues, captureBaseline]);

  const handleSelectThumbnail = (selectedFile) => {
    setFile(selectedFile);
    setImageView(URL.createObjectURL(selectedFile));
  };

  const handleRemoveThumbnail = () => {
    setFile(null);
    setImageView("");
  };

  const isAllFieldsFilled = Boolean(
    title &&
      selectedCategory !== "" &&
      content &&
      engTitle &&
      engContent &&
      (file || imageView),
  );
  const canSubmit = isAllFieldsFilled && isDirty && !submitting;

  const handleLeave = async () => {
    const shouldLeave = await confirmLeaveIfDirty(isDirty, copy.leave);
    if (shouldLeave) navigate("/shopManager");
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (
      content.length > SHOP_CONTENT_MAX_LENGTH ||
      engContent.length > SHOP_ENG_CONTENT_MAX_LENGTH
    ) {
      alertDialog(
        `내용은 ${SHOP_CONTENT_MAX_LENGTH}자, 영문 내용은 ${SHOP_ENG_CONTENT_MAX_LENGTH}자까지 입력할 수 있습니다.`,
        { title: "글자 수 제한 초과" },
      );
      return;
    }

    const shouldProceed = await confirmDialog(copy.confirm);
    if (!shouldProceed) return;

    try {
      setSubmitting(true);
      // 서버가 imageKey 필드에 S3 key를 기대한다.
      // create는 항상 새 파일 업로드, edit는 새 파일이 있으면 교체·없으면 기존 이미지(URL→key) 유지.
      let imageKey = mode === "edit" ? toFileKey(imageView) : "";
      if (file) {
        const uploaded = await uploadFileWithPresigned(file, true);
        imageKey = uploaded?.key || "";
        if (!imageKey) {
          alertDialog("이미지 업로드 실패");
          return;
        }
      }
      if (!imageKey) {
        alertDialog("이미지를 넣어주세요!");
        return;
      }

      const data = {
        name,
        imageKey,
        // 폼에 hide 토글이 없어 미전송 시 서버가 hide=null로 덮어쓴다.
        // 등록(hide=false)과 동일하게 명시적으로 false를 보내 null 변질을 막는다.
        hide: false,
        pin,
        inProgress,
        title,
        content,
        category: selectedCategory,
        engTitle,
        engContent,
      };
      await onSubmit(data);
      if (copy.successAlert) {
        await alertDialog(copy.successAlert, { title: "등록 완료" });
      }
      navigate("/shopManager");
    } catch (error) {
      alertDialog("저장 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.", {
        title: `HGU샵 ${copy.submit.replace("하기", "")} 실패`,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormPageShell
      title={copy.header}
      onBack={handleLeave}
      isLoading={isLoading}
      loadError={loadError}
      submitLabel={copy.submit}
      canSubmit={canSubmit}
      onSubmit={handleSubmit}
    >
      <CircularProgressIndicator loading={submitting} msg="저장 중.." />
      <RegisterContentsDiv>
        <RegisterDiv>
          <RowDiv>
            <Title>
              제휴상품<RedAsterisk>*</RedAsterisk>
            </Title>
            <TextInput
              type="text"
              placeholder="제목입력"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </RowDiv>
          <RowDiv>
            <Title>
              카테고리<RedAsterisk>*</RedAsterisk>
            </Title>
            <ChipGroup
              options={CATEGORIES}
              value={selectedCategory}
              onChange={(value) => setSelectedCategory(value ?? "")}
            />
          </RowDiv>
          <RowDiv>
            <Title>
              메인노출<RedAsterisk>*</RedAsterisk>
            </Title>
            <ChipGroup
              options={[
                { label: "Yes", value: true },
                { label: "No", value: false },
              ]}
              value={pin}
              onChange={setPin}
              allowDeselect={false}
            />
          </RowDiv>
          {/* 등록은 항상 '제휴중'(inProgress=true)으로 담으므로 선택 행은 수정에서만 노출 */}
          {mode === "edit" && (
            <RowDiv>
              <Title>
                제휴 상태<RedAsterisk>*</RedAsterisk>
              </Title>
              <ChipGroup
                options={[
                  { label: "제휴중", value: true },
                  { label: "종료", value: false },
                ]}
                value={inProgress}
                onChange={setInProgress}
                allowDeselect={false}
              />
            </RowDiv>
          )}
        </RegisterDiv>
        <FilePutDiv>
          <ImageUploadBox
            imageUrl={imageView}
            onSelectFile={handleSelectThumbnail}
            onRemove={handleRemoveThumbnail}
            title="썸네일 이미지 삽입"
            sizeHint="이미지 사이즈(250*250 권장)"
            alt="uploaded"
          />
          <ThumbnailText>
            썸네일 이미지는 본문에 노출되지 않습니다.
          </ThumbnailText>
        </FilePutDiv>
      </RegisterContentsDiv>
      <TextEditor>
        <CKEditor
          editor={ClassicEditor}
          data={content}
          onChange={(_, editor) => setContent(editor.getData())}
          onReady={(editor) => {
            // 한글 IME 조합 종료 시 onChange가 누락될 수 있어 한 번 더 동기화.
            editor.editing.view.document.on("compositionend", () => {
              setContent(editor.getData());
            });
          }}
          onBlur={(_, editor) => setContent(editor.getData())}
          config={buildEditorConfig("내용을 입력하세요.")}
          language="ko"
        />
        <LengthInfo $isOver={content.length > SHOP_CONTENT_MAX_LENGTH}>
          {content.length} / {SHOP_CONTENT_MAX_LENGTH}
        </LengthInfo>
      </TextEditor>
      <RowDiv style={{ margin: "20px 0" }}>
        <Header3>English</Header3>
        <Title>
          상단의 카테고리, 메인노출 여부, 제휴기간, 썸네일은 영문 페이지에서
          동일하게 적용됩니다
        </Title>
      </RowDiv>
      <RegisterContentsDiv>
        <RegisterDiv>
          <RowDiv>
            <Title>
              Title<RedAsterisk>*</RedAsterisk>
            </Title>
            <TextInput
              type="text"
              placeholder="Write a title"
              value={engTitle}
              onChange={(e) => setEngTitle(e.target.value)}
            />
          </RowDiv>
        </RegisterDiv>
      </RegisterContentsDiv>
      <TextEditor>
        <CKEditor
          editor={ClassicEditor}
          data={engContent}
          onChange={(_, editor) => setEngContent(editor.getData())}
          config={buildEditorConfig("Write a content.")}
          language="en"
        />
        <LengthInfo $isOver={engContent.length > SHOP_ENG_CONTENT_MAX_LENGTH}>
          {engContent.length} / {SHOP_ENG_CONTENT_MAX_LENGTH}
        </LengthInfo>
      </TextEditor>
    </FormPageShell>
  );
}

export default ShopForm;

const RegisterContentsDiv = styled.div`
  display: flex;
  width: 100%;
  background-color: white;
`;

const RegisterDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 20px;
  gap: 15px;
`;

const RowDiv = styled.div`
  display: flex;
  flex-direction: row;
  width: 517px;
  gap: 40px;
  justify-content: flex-start;
  align-items: center;
`;

const Title = styled.div`
  width: fit-content;
  white-space: nowrap;
  color: var(--text-black, #111);
  font-family: "Noto Sans KR";
  font-size: 16px;
  font-weight: 400;
  line-height: 140%;
`;

const RedAsterisk = styled.span`
  font-size: ${(props) => props.theme.Web_fontSizes.Body3};
  font-weight: ${(props) => props.theme.fontWeights.Body3};
  line-height: ${(props) => props.theme.LineHeight.Body3};
  letter-spacing: -0.56px;
  font-style: normal;
  font-family: "Noto Sans KR";
  text-align: center;
  color: red;
`;

const Header3 = styled.header`
  color: #000;
  font-family: "NotoSansKRBold";
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
`;

const TextInput = styled.input`
  width: 100%;
  height: 35px;
  border-radius: 2px;
  border: 1px solid #e8e8e8;
  padding: 2px 10px;
  &::placeholder {
    opacity: 0.2;
  }
`;


const FilePutDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ThumbnailText = styled.p`
  color: var(--black, #000);
  font-family: "Noto Sans KR";
  font-size: ${(props) => props.theme.Web_fontSizes.Body3};
  font-weight: ${(props) => props.theme.fontWeights.Body3};
  line-height: ${(props) => props.theme.LineHeight.Body3};
  letter-spacing: -0.56px;
  opacity: 0.4;
  margin-bottom: 20px;
  margin-right: 2px;
  white-space: nowrap;
`;

const TextEditor = styled.div`
  width: 100%;
  margin-top: 20px;

  .ck-editor__editable {
    height: 310px !important;
    overflow-y: scroll;
    scrollbar-width: thin;
    scrollbar-color: #ccc transparent;
  }
`;

const LengthInfo = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
  font-size: 13px;
  color: ${(props) => (props.$isOver ? "#d11a2a" : "#666")};
`;
